import {
  Button,
  CIActionGuard,
  Modal,
  useUserPermission
} from 'ccs-ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DropdownMenuButton from '../../../../../common/components/UI/menu/DropdownMenuButton';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import { REGISTRY_TYPE } from '../../../../../dashboard/constants/CommonConstants';
import BackIcon from '../../../../_components/icons/BackIcon';
import RejectIcon from '../../../../_components/icons/RejectIcon';
import UpdateIcon from '../../../../_components/icons/UpdateIcon';
import { STATUS_TYPES } from '../../../../constants';
import { NavBarState } from '../../../../models';
import { IServiceStatusHistoryModel } from '../../../../models/services/IServiceResponseModel';
import { useSelector } from '../../../../store/AppStore';
import {
  getServiceNameFromUrl,
  getVersionFromUrl
} from '../../../../utilities/services/ServiceListUtils';
import classes from '../ServiceWrapper.module.scss';

interface IServicePatchButtonsProps {
  isEditService: boolean;
  status: string;
  backToServiceBtnHandler: () => void;
  callUpdateServiceStatusApi: (status: string) => void;
  enableEditService: () => void;
  updateServiceToDraftState: () => void;
  onRejectClick: () => void;
  btnInvalid: boolean;
  handleReturnButton: (value: boolean) => void;
  serviceCommentHistoryCheck?: IServiceStatusHistoryModel[];
  onDeleteSuccess: () => void;
  onDependencyGraphFound: (isDependencyGraphFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => void
}

const ServicePatchButtons = ({ isEditService, status, backToServiceBtnHandler, enableEditService, callUpdateServiceStatusApi, updateServiceToDraftState, onRejectClick, btnInvalid, handleReturnButton, serviceCommentHistoryCheck = undefined, onDeleteSuccess, onDependencyGraphFound }: IServicePatchButtonsProps) => {
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();
  const { isAddNewServiceOperation, isEditServiceOperation } = useSelector(
    (state) => state.serviceOperationMonitorReducer
  );
  const { serviceDetails: navBarServiceDetails } = useSelector(
    (state) => state.navBarStateReducer
  );
  const version = getVersionFromUrl();
  const { serviceDetails } = useSelector(
    (state) => state.getServiceNameReducer
  );
  const serviceName = getServiceNameFromUrl();

  const [returnedDialogOpen, setReturnedDialogOpen] = useState<boolean>(false);
  const [showModalSendForApproval, setShowModalSendForApproval] = useState<boolean>(false);
  const [showModalApproveAction, setShowModalApproveAction] = useState<boolean>(false);
  const [showModalEditAction, setShowModalEditAction] = useState<boolean>(false);

  /**
   * @method getDraftStatusButtons
   * @description Renders the buttons for draft status
   * @returns UI button components
   */
  const getDraftStatusButtons = () => (
    <div>
      {status?.toLocaleUpperCase() === STATUS_TYPES.DRAFT
        && CIActionGuard(
          [
            requiredAuthPermission.Services.edit,
            requiredAuthPermission.Services.approve
          ],
          permissionObject.permission
        ) && (
          <div>
            {isEditService ? (
              <Button
                className={classes['btn-add']}
                disabled={
                  btnInvalid
                  || isAddNewServiceOperation
                  || isEditServiceOperation
                }
                variant="primary"
                onClick={updateServiceToDraftState}
                data-testid="btn-update-service"
              >
                {translate('service_wrapper_component.lbl_update')}
              </Button>
            ) : (
              <>
                <Button
                  className={classes['btn-add']}
                  variant="primary"
                  onClick={enableEditService}
                  data-testid="btn-edit"
                >
                  {translate('service_wrapper_component.lbl_edit')}
                </Button>
                <Button
                  className={classes['btn-edit']}
                  variant="primary"
                  onClick={() => setShowModalSendForApproval(true)}
                  data-testid="btn-submit-for-review"
                >
                  {translate('events_wrapper.lbl_submit_for_review')}
                </Button>
              </>
            )}
          </div>
        )}
    </div>
  );

  /**
   * @method getReadyToReviewStatusButtons
   * @description Renders the buttons for ready_for_review status
   * @returns UI button components
   */
  const getReadyToReviewStatusButtons = () => (
    <div>
      {status?.toLocaleUpperCase() === STATUS_TYPES.PENDING_APPROVAL && (
        <>
          {CIActionGuard(
            [
              requiredAuthPermission.Services.edit,
              requiredAuthPermission.Services.approve
            ],
            permissionObject.permission
          )
            && (
              <Button
                className={classes['btn-add']}
                variant="primary"
                onClick={() =>
                  callUpdateServiceStatusApi(STATUS_TYPES.REVIEW_CANCEL)}
                data-testid="btn-cancel_review"
              >
                {translate('events_wrapper.lbl_cancel_review')}
              </Button>
            )}
          {CIActionGuard(
            [requiredAuthPermission.Services.approve],
            permissionObject.permission
          )
            && (
              <>
                <Button
                  className={classes['btn-approve']}
                  variant="primary"
                  disabled={navBarServiceDetails.state !== NavBarState.COMPLETE}
                  onClick={() => setShowModalApproveAction(true)}
                  data-testid="btn-aprove"
                >
                  <UpdateIcon /> {'    '}
                  {translate('events_wrapper.lbl_approve')}
                </Button>
                <Button
                  className={classes['btn-reject']}
                  variant="secondary"
                  onClick={onRejectClick}
                  data-testid="btn-reject"
                >
                  <BackIcon /> {'    '} {translate('events_wrapper.lbl_reject')}
                </Button>
              </>
            )}
        </>
      )}
    </div>
  );

  /**
   * @method getReturnedOrActiveStatusButtons
   * @description Renders the buttons for returned or active status
   * @returns UI button components
   */
  const getReturnedOrActiveStatusButtons = () => {
    const statusList = [STATUS_TYPES.RETURNED, STATUS_TYPES.ACTIVE, STATUS_TYPES.INACTIVE];
    if (statusList.includes(status?.toLocaleUpperCase())
      && CIActionGuard(
        [
          requiredAuthPermission.Services.edit,
          requiredAuthPermission.Services.approve
        ],
        permissionObject.permission
      )
    ) {
      if (isEditService) {
        return (
          <Button
            className={classes['btn-add']}
            disabled={
              btnInvalid || isAddNewServiceOperation || isEditServiceOperation
            }
            variant="primary"
            onClick={updateServiceToDraftState}
            data-testid="btn-update-service"
          >
            {translate('service_wrapper_component.lbl_update')}
          </Button>
        );
      }
      if (status?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE) {
        return (
          <Button
            className={classes['btn-edit']}
            variant="primary"
            onClick={() => setShowModalEditAction(true)}
            data-testid="btn-edit"
          >
            {translate('service_wrapper_component.lbl_edit')}
          </Button>
        );
      }

      if (status?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE) {
        return (
          <Button
            className={classes['btn-add']}
            variant="primary"
            onClick={() =>
              callUpdateServiceStatusApi(STATUS_TYPES.DRAFT)}
            data-testid="btn-cancel_review"
          >
            {translate('events_wrapper.lbl_cancel_review')}
          </Button>
        );
      }
      return (
        <Button
          className={classes['btn-add']}
          variant="primary"
          onClick={enableEditService}
          data-testid="btn-edit"
        >
          {translate('service_wrapper_component.lbl_edit')}
        </Button>
      );
    }

    return null;
  };

  const handleReturnedButtonOpen = () => {
    setReturnedDialogOpen(!returnedDialogOpen);
    handleReturnButton(!returnedDialogOpen);
  };

  return (
    <>
      <div className={`${classes['btn-container']} ci-button-group`}>
        {serviceCommentHistoryCheck?.length !== 0
          && status?.toLocaleUpperCase() === STATUS_TYPES.RETURNED
          && !isEditService && (
            <Button
              className={classes['btn-reject-with-icon']}
              variant="secondary"
              onClick={handleReturnedButtonOpen}
              data-testid="btn-wrapper-cancel"
            >
              {' '}
              <RejectIcon />
            </Button>
          )}
        <Button
          className={classes['btn-cancel']}
          variant="secondary"
          onClick={backToServiceBtnHandler}
          data-testid="btn-back-to-service"
        >
          {isEditService
            ? translate('events_wrapper.lbl_cancel_btn')
            : translate('events_wrapper.lbl_back_btn')}
        </Button>
        {getDraftStatusButtons()}
        {getReadyToReviewStatusButtons()}
        {getReturnedOrActiveStatusButtons()}
        {!isEditService && (
          <DropdownMenuButton
            status={status}
            deleteURL={`services/${serviceName}/version/${version}`}
            onDeleteSuccess={onDeleteSuccess}
            registryType={REGISTRY_TYPE.SERVICE}
            entityData={serviceDetails[0]}
            registryName={serviceDetails[0]?.serviceName}
            onDependencyGraphFound={onDependencyGraphFound}
          />
        )}
      </div>
      <Modal
        modalShow={showModalSendForApproval}
        modalTitle={translate('events_wrapper.lbl_submit_for_review')}
        modalBody={translate('service_wrapper.msg_send_for_approval')}
        btnSaveText={String(translate('comment_dialog.lbl_yes'))}
        btnCloseText={String(translate('comment_dialog.lbl_no'))}
        onSaveModal={() => {
          callUpdateServiceStatusApi(STATUS_TYPES.PENDING_APPROVAL);
          setShowModalSendForApproval(false);
        }}
        onCloseModal={() => setShowModalSendForApproval(false)}
        alignToCentre
        data-testid="activeStatusEditWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />

      <Modal
        modalShow={showModalApproveAction}
        modalTitle={translate('events_wrapper.lbl_approve')}
        modalBody={translate('service_wrapper.msg_approve')}
        btnSaveText={String(translate('comment_dialog.lbl_yes'))}
        btnCloseText={String(translate('comment_dialog.lbl_no'))}
        onSaveModal={() => {
          callUpdateServiceStatusApi(STATUS_TYPES.APPROVED);
          setShowModalApproveAction(false);
        }}
        onCloseModal={() => setShowModalApproveAction(false)}
        alignToCentre
        data-testid="activeStatusEditWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
      <Modal
        modalShow={showModalEditAction}
        modalTitle={translate('service_wrapper.lbl_new_version')}
        modalBody={translate('service_wrapper.msg_new_version')}
        btnSaveText={String(translate('comment_dialog.lbl_proceed'))}
        btnCloseText={String(translate('comment_dialog.lbl_close'))}
        onSaveModal={() => {
          enableEditService();
          setShowModalEditAction(false);
        }}
        onCloseModal={() => setShowModalEditAction(false)}
        alignToCentre
        data-testid="activeStatusEditWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
    </>
  );
};

export default ServicePatchButtons;
